import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import GameMenu from "../components/GameMenu";
import Timer from "../components/Timer";
import EndGameBtn from "../components/EndGameBtn";
import Popup from "../components/Popup";
import LeaveGamePopup from "../components/LeaveGamePopup";
import "../assets/css/mixedwords.css";
import api from "../api/client";
// import AnimatedBg from "../components/AnimatedBg";

const GRID_SIZE = 10;
const FALLBACK_WORDS = ["PREDICT", "PREVENT", "SCHEDULE", "EMPLOY"];
const MIN_FETCH_INTERVAL_MS = 30_000;

//  Tous les guards sur `window` — survivent au HMR webpack ET aux remounts React.
// Un useRef repart à null à chaque remount → inutile comme guard inter-mount.
if (!window.__MCQ_CACHE__)        window.__MCQ_CACHE__        = new Map();
if (!window.__INFLIGHT__)         window.__INFLIGHT__         = new Map();
if (!window.__LAST_REQUEST_AT__)  window.__LAST_REQUEST_AT__  = new Map();
if (!window.__MCQ_LOADED__)       window.__MCQ_LOADED__       = new Map(); // ← clé du fix

const MCQ_CACHE       = window.__MCQ_CACHE__;
const INFLIGHT        = window.__INFLIGHT__;
const LAST_REQUEST_AT = window.__LAST_REQUEST_AT__;
const MCQ_LOADED      = window.__MCQ_LOADED__; // queryKey → { grid, motsPlaces }

// ─── Fonctions pures — hors du composant ────────────────────────────────────

function normalizeWord(w) {
  return String(w || "").toUpperCase().replace(/\s/g, "");
}

function parseRetryAfterSeconds(detail) {
  const m = /Expected available in (\d+)\s*seconds?/i.exec(detail || "");
  return m ? Number(m[1]) : null;
}

function canPlaceWord(grid, word, row, col, dir) {
  const size = grid.length;
  if (dir === "H") {
    if (col + word.length > size) return false;
    for (let i = 0; i < word.length; i++) {
      const cell = grid[row][col + i];
      if (cell !== "" && cell !== word[i]) return false;
    }
  } else {
    if (row + word.length > size) return false;
    for (let i = 0; i < word.length; i++) {
      const cell = grid[row + i][col];
      if (cell !== "" && cell !== word[i]) return false;
    }
  }
  return true;
}

function placeWord(grid, word, row, col, dir) {
  if (dir === "H") {
    for (let i = 0; i < word.length; i++) grid[row][col + i] = word[i];
  } else {
    for (let i = 0; i < word.length; i++) grid[row + i][col] = word[i];
  }
}

function generateGrid(size, words) {
  const grid = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => "")
  );
  const positions = [];

  for (const rawWord of words) {
    const word = normalizeWord(rawWord);
    if (!word) continue;
    let placed = false, attempts = 0;
    while (!placed && attempts < 200) {
      const dir = Math.random() < 0.5 ? "H" : "V";
      const row = Math.floor(Math.random() * size);
      const col = Math.floor(Math.random() * size);
      if (canPlaceWord(grid, word, row, col, dir)) {
        placeWord(grid, word, row, col, dir);
        positions.push({ word, row, col, dir });
        placed = true;
      }
      attempts++;
    }
  }

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  for (let r = 0; r < size; r++)
    for (let c = 0; c < size; c++)
      if (grid[r][c] === "")
        grid[r][c] = alphabet[Math.floor(Math.random() * alphabet.length)];

  return { grid, positions };
}

function getCellsBetween(start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (!(dx === 0 || dy === 0 || Math.abs(dx) === Math.abs(dy))) return null;
  const length = Math.max(Math.abs(dx), Math.abs(dy));
  const stepX  = dx === 0 ? 0 : dx / Math.abs(dx);
  const stepY  = dy === 0 ? 0 : dy / Math.abs(dy);
  const cells = [];
  for (let i = 0; i <= length; i++) {
    const x = start.x + i * stepX;
    const y = start.y + i * stepY;
    if (x >= 0 && y >= 0 && x < GRID_SIZE && y < GRID_SIZE)
      cells.push({ x, y });
  }
  return cells;
}

function drawMissingWordLine(startCell, endCell) {
  if (!startCell || !endCell) return;

  const container = document.getElementById("mixedwords_grid_container");
  if (!container) return;

  const containerRect = container.getBoundingClientRect();
  const startRect = startCell.getBoundingClientRect();
  const endRect = endCell.getBoundingClientRect();

  // centre de la cellule de départ (relatif au container)
  const x1 = startRect.left - containerRect.left + startRect.width / 2;
  const y1 = startRect.top - containerRect.top + startRect.height / 2;

  // centre de la cellule de fin (relatif au container)
  const x2 = endRect.left - containerRect.left + endRect.width / 2;
  const y2 = endRect.top - containerRect.top + endRect.height / 2;

  const dx = x2 - x1;
  const dy = y2 - y1;

  const angle = Math.atan2(dy, dx);
  const baseLength = Math.sqrt(dx ** 2 + dy ** 2);

  // taille réelle des cellules → fonctionne aussi en responsive
  const startCellSize = Math.min(startRect.width, startRect.height);
  const endCellSize = Math.min(endRect.width, endRect.height);

  // on fait dépasser la ligne d’une demi-cellule de chaque côté
  const extraStart = startCellSize / 2;
  const extraEnd = endCellSize / 2;

  const totalLength = baseLength + extraStart + extraEnd;

  // on recule le point de départ dans la direction opposée
  const offsetX = Math.cos(angle) * extraStart;
  const offsetY = Math.sin(angle) * extraStart;

  // hauteur de ligne adaptative selon la taille réelle des cellules
  const lineThickness = Math.min(startCellSize, endCellSize) * 0.8;

  const line = document.createElement("div");
  line.classList.add("missing-word-line");

  line.style.width = `${totalLength}px`;
  line.style.height = `${lineThickness}px`;

  let visualAdjustX = 0;
  let visualAdjustY = 0;

  // verticale → 2px à gauche
  if (Math.abs(dx) < 1) {
    visualAdjustX = -2;
  }
  // horizontale → 2px en haut
  else if (Math.abs(dy) < 1) {
    visualAdjustY = -2;
  }

  line.style.left = `${x1 - offsetX + visualAdjustX}px`;
  line.style.top = `${y1 - offsetY + visualAdjustY}px`;

  line.style.transform = `translateY(-50%) rotate(${angle}rad)`;
  line.style.transformOrigin = "0 50%";

  container.appendChild(line);
}

// ─── Composant ───────────────────────────────────────────────────────────────

export default function MixedWords() {
  const [timerIsFinished, setTimerIsFinished] = useState(false);
  const [timeElapsed, setTimeElapsed]         = useState(0);
  const [leaveGamePopup, setLeaveGamePopup]   = useState(false);
  const [grid, setGrid]                       = useState([]);
  const [motsPlaces, setMotsPlaces]           = useState([]);
  const [selection, setSelection]             = useState({ start: null, end: null });
  const [selectedCells, setSelectedCells]     = useState([]);
  const [wrongWords, setWrongWords]           = useState([]);
  const [foundWords, setFoundWords]           = useState([]);
  const [highlightedCells, setHighlightedCells] = useState([]);
  const [errorCount, setErrorCount]           = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [wordData, setWordData] = useState([]);

  const cellRefs        = useRef([]);
  const getTimerTimeRef = useRef(null);

  const location = useLocation();
  const { theme, subTheme } = location.state || {};

  // Stabilisé avec useMemo — évite de recalculer queryKey à chaque render
  const API_BASE = useMemo(() =>
    (window?.__ENV__?.API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "").trim()
  , []); // [] = calculé une seule fois au montage

  //  Callbacks stables — ne recréent pas de nouvelles références à chaque render,
  // ce qui évitait de redémarrer le setInterval du Timer toutes les 250ms.
  const sendTime        = useCallback((time) => setTimeElapsed(time), []);
  const handleLeaveGame = useCallback((v) => setLeaveGamePopup(v), []);
  const handleGetTime   = useCallback((fn) => { getTimerTimeRef.current = fn; }, []);

  const checkErrors = useCallback(() => {
    setFoundWords((currentFound) => {
      setMotsPlaces((currentMots) => {
        if (currentFound.length !== currentMots.length) {
          setErrorCount((prev) => prev + (currentMots.length - currentFound.length));
        }
        return currentMots;
      });
      return currentFound;
    });
  }, []);

  const handleFinish = useCallback(() => {
    checkErrors();
    setTimerIsFinished(true);
  }, [checkErrors]);

  //  Handlers souris stables avec setters fonctionnels — pas de dépendances volatiles
  const handleMouseDown = useCallback((x, y) => {
    setSelection({ start: { x, y }, end: null });
    setSelectedCells([{ x, y }]);
  }, []);

  const handleMouseEnter = useCallback((x, y) => {
    setSelection((prev) => {
      if (!prev.start) return prev;
      const cells = getCellsBetween(prev.start, { x, y });
      if (!cells) return prev;
      setSelectedCells(cells);
      return { ...prev, end: { x, y } };
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    setSelectedCells((currentSelected) => {
      if (currentSelected.length > 0) {
        setGrid((currentGrid) => {
          const selectedWord = currentSelected.map((c) => currentGrid[c.x][c.y]).join("");
          const reversedWord = selectedWord.split("").reverse().join("");

          setMotsPlaces((currentMots) => {
            const allowed = (currentMots?.length ? currentMots : FALLBACK_WORDS).map(normalizeWord);
            const isDirect  = allowed.includes(selectedWord);
            const isReverse = allowed.includes(reversedWord);

            if (isDirect || isReverse) {
              const matched = isDirect ? selectedWord : reversedWord;
              setFoundWords((prev) => {
                if (prev.includes(matched)) return prev;
                setHighlightedCells((h) => [...h, ...currentSelected]);
                const start = currentSelected[0];
                const end   = currentSelected[currentSelected.length - 1];
                drawMissingWordLine(
                  cellRefs.current?.[start.x]?.[start.y],
                  cellRefs.current?.[end.x]?.[end.y]
                );
                return [...prev, matched];
              });
            } else if (selectedWord.length > 3) {
              setWrongWords((prev) => [...prev, selectedWord]);
              setErrorCount((prev) => prev + 1);
            }
            return currentMots;
          });
          return currentGrid;
        });
      }
      return [];
    });
    setSelection({ start: null, end: null });
  }, []);

  // ─── Dérivés ──────────────────────────────────────────────────────────────

  const allowedWords = useMemo(() => {

    return (motsPlaces?.length ? motsPlaces : FALLBACK_WORDS).map(normalizeWord);
  }, [motsPlaces]);


  // ── Enregistrement session ────────────────────────────────────────
  const saveSession = async (timeSeconds) => {
    try {
      await api.post('/api/gameplay/session/', {
        game_code:    'word_search',
        theme:        theme,
        sous_theme:   subTheme,
        level:        'B1',
        score:        foundWords.length,
        total:        motsPlaces.length,
        correct:      foundWords.length,
        wrong:        motsPlaces.length - foundWords.length,
        time_seconds: timeSeconds || timeElapsed,
      });
    } catch (e) {
      console.warn('Session non enregistree :', e);
    }
  };

  const queryKey = useMemo(() => {
    const t  = theme    || "Professional Skills";
    const st = subTheme || "INVESTISSEMENT";
    return `${API_BASE}::B1::${t}::${st}::6`;
  }, [API_BASE, theme, subTheme]);

  const mcqUrl = useMemo(() => {
    const t  = theme    || "Professional Skills";
    const st = subTheme || "INVESTISSEMENT";
    return (
      `${API_BASE}/api/games/mcq/` +
      `?level=B1` +
      `&theme=${encodeURIComponent(t)}` +
      `&sous_theme=${encodeURIComponent(st)}` +
      `&n=6`
    );
  }, [API_BASE, theme, subTheme]);

  // ─── Chargement MCQ ───────────────────────────────────────────────────────

  useEffect(() => {
    //  GUARD PRINCIPAL — window-level, survit aux remounts ET au HMR.
    // Ancienne approche (useRef) : repart à null à chaque remount → inutile.
    // Si cette queryKey a déjà été chargée : réappliquer depuis MCQ_LOADED,
    // sans resetUI(), sans fetch, sans aucun side-effect visible.
    const alreadyLoaded = MCQ_LOADED.get(queryKey);
    if (alreadyLoaded) {
      setGrid(alreadyLoaded.grid);
      setMotsPlaces(alreadyLoaded.motsPlaces);
      setWordData(alreadyLoaded.wordData || []);
      setApiError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const CACHE_TTL_MS = 300_000; // 5 min

    const resetUI = () => {
      setLoading(true);
      setApiError(null);
      setGrid([]);
      setMotsPlaces([]);
      setWordData([]);
      setFoundWords([]);
      setWrongWords([]);
      setHighlightedCells([]);
      setSelection({ start: null, end: null });
      setSelectedCells([]);
      setTimerIsFinished(false);
      setErrorCount(0);
      document.querySelectorAll(".missing-word-line").forEach((l) => l.remove());
    };

    function buildGridFromWords(words) {
      const source = words?.length
        ? words
        : FALLBACK_WORDS.map((w) => ({ word: w, trad: w }));

      const normalizedWords = source
        .map((item) => ({
          word: normalizeWord(typeof item === "string" ? item : item.word),
          trad: typeof item === "string" ? item : (item.trad || item.word),
        }))
        .filter((item) => item.word);

      const { grid: newGrid, positions } = generateGrid(
        GRID_SIZE,
        normalizedWords.map((w) => w.word)
      );

      return {
        grid: newGrid,
        motsPlaces: positions.map((p) => p.word),
        wordData: normalizedWords,
      };
    }

    const applyResult = (result) => {
      if (cancelled || !result) return;

      MCQ_LOADED.set(queryKey, {
        grid: result.grid,
        motsPlaces: result.motsPlaces,
        wordData: result.wordData || [],
      });

      setGrid(result.grid);
      setMotsPlaces(result.motsPlaces);
      setWordData(result.wordData || []);
      setApiError(null);
      setLoading(false);
    };

    async function loadOnce() {
      resetUI();
      const now = Date.now();

      // 1. Trop tôt depuis la dernière requête → cache ou fallback immédiat
      const last = LAST_REQUEST_AT.get(queryKey);
      if (last && now - last < MIN_FETCH_INTERVAL_MS) {
        const hit = MCQ_CACHE.get(queryKey);
        applyResult(hit
          ? { grid: hit.grid, motsPlaces: hit.motsPlaces }
          : buildGridFromWords(FALLBACK_WORDS)
        );
        return;
      }

      // 2. Cache frais disponible
      const cached = MCQ_CACHE.get(queryKey);
      if (cached && now - cached.ts < CACHE_TTL_MS) {
        applyResult({ grid: cached.grid, motsPlaces: cached.motsPlaces });
        return;
      }

      // 3. Requête déjà en vol → attendre la même promise
      if (INFLIGHT.has(queryKey)) {
        try { applyResult(await INFLIGHT.get(queryKey)); } catch { /* ignore */ }
        return;
      }

      LAST_REQUEST_AT.set(queryKey, now);

      const promise = (async () => {
        try {
          const res = await fetch(mcqUrl, { signal: controller.signal });

          // 429 — fallback immédiat + retry différé après le délai indiqué
          if (res.status === 429) {
            const body    = await res.json().catch(() => ({}));
            const waitSec = parseRetryAfterSeconds(body?.detail) ?? 10;
            const fallback = buildGridFromWords(FALLBACK_WORDS);
            MCQ_CACHE.set(queryKey, { ts: Date.now(), ...fallback });

            setTimeout(async () => {
              try {
                if (cancelled) return;
                const last2 = LAST_REQUEST_AT.get(queryKey);
                if (last2 && Date.now() - last2 < MIN_FETCH_INTERVAL_MS) return;
                LAST_REQUEST_AT.set(queryKey, Date.now());
                const res2 = await fetch(mcqUrl, { signal: controller.signal });
                if (!res2.ok) return;
                const data2  = await res2.json();
                const items2 = Array.isArray(data2) ? data2 : data2?.items || [];
                const seen2 = new Set();
                const words2 = items2
                  .map((it) => {
                    const word = normalizeWord(it?.answer ?? it?.target_word ?? "");
                    const trad = it?.translation_fr || it?.translation || word;
                    return { word, trad };
                  })
                  .filter((item) => item.word && !seen2.has(item.word) && seen2.add(item.word));

                const ok2 = buildGridFromWords(words2);
                MCQ_CACHE.set(queryKey, { ts: Date.now(), ...ok2 });
                applyResult(ok2);
              } catch { /* ignore */ }
            }, waitSec * 1000);

            return fallback;
          }

          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.detail || `HTTP ${res.status}`);
          }

          const data  = await res.json();
          const items = Array.isArray(data) ? data : data?.items || [];
          const seen = new Set();
          const words = items
            .map((it) => {
              const word = normalizeWord(it?.answer ?? it?.target_word ?? "");
              const trad = it?.translation_fr || it?.translation || word;
              return { word, trad };
            })
            .filter((item) => item.word && !seen.has(item.word) && seen.add(item.word));

          const ok = buildGridFromWords(words);
          MCQ_CACHE.set(queryKey, { ts: Date.now(), ...ok });
          return ok;

        // } catch (e) {
        //   if (e?.name === "AbortError") return null;
        //   const fallback = buildGridFromWords(FALLBACK_WORDS);
        //   MCQ_CACHE.set(queryKey, { ts: Date.now(), ...fallback });
        //   return fallback;
        } catch (e) {
          if (e?.name === "AbortError") return null;

          setApiError(e.message || "Erreur lors du chargement");
          setLoading(false);

          return null;
        } finally {
          INFLIGHT.delete(queryKey);
        }
      })();

      INFLIGHT.set(queryKey, promise);
      applyResult(await promise);
    }

    loadOnce();
    return () => { cancelled = true; controller.abort(); };
  }, [queryKey, mcqUrl]);

  // ─── Fin de jeu automatique ───────────────────────────────────────────────

  useEffect(() => {
    if (motsPlaces.length > 0 && foundWords.length === motsPlaces.length) {
      if (getTimerTimeRef.current) {
        sendTime(getTimerTimeRef.current());
      }
      setTimerIsFinished(true);
    }
  }, [foundWords.length, motsPlaces.length, sendTime]);

  // ─── Rendu ────────────────────────────────────────────────────────────────

  return (
    <div className="page-container">
      <Sidebar />

      <div className="content-container inGame" id="mixedWords">
        {loading ? (
            <div className="loading-container">
                <h1>Chargement...</h1>
            </div>
        ) : apiError ? (
            <div className="error-container">
                <h2>Une erreur est survenue lors du chargement des données.</h2>
                <button className="mode-btn active" onClick={() => window.location.reload()}>
                    Réessayer
                </button>
            </div>
        ) : (
            <>
            <div className="title">
              <h4>{theme} - {subTheme}</h4>
              <div className="level-infos">
                <Timer
                  timer={timerIsFinished}
                  onFinish={handleFinish}
                  onSendTime={sendTime}
                  onGetTime={handleGetTime}
                  hasLimit={false}
                />
              </div>
            </div>

            <div className="game-container launched">
              <div className="mixedwords_game_container launched">

                <div id="mixedwords_failed_words_container" className="mixedwords_failed_word_container">
                  {wrongWords.map((mot, i) => (
                    <span key={i} className="wrong-word">{mot}</span>
                  ))}
                </div>

                <div className="flex-grow-1" id="mixedwords_grid_container">
                  <div className="grid">
                    {grid.map((row, x) =>
                      row.map((cell, y) => {
                        const isSelected    = selectedCells.some((c) => c.x === x && c.y === y);
                        const isHighlighted = highlightedCells.some((c) => c.x === x && c.y === y);
                        return (
                          <div
                            className={`cell justify-content-center${isSelected ? " selected" : ""}${isHighlighted ? " text-white" : ""}`}
                            key={`${x}-${y}`}
                            onMouseDown={() => handleMouseDown(x, y)}
                            onMouseEnter={() => handleMouseEnter(x, y)}
                            onMouseUp={handleMouseUp}
                            data-x={x}
                            data-y={y}
                            ref={(el) => {
                              if (!cellRefs.current[x]) cellRefs.current[x] = [];
                              cellRefs.current[x][y] = el;
                            }}
                          >
                            {cell}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <div id="mixedwords_words_container" className="mixedwords_word_container">
                  {allowedWords.map((mot, i) => {
                    const original = wordData.find((w) => w.word === mot);
                    const trad = original ? original.trad : mot;

                    return (
                      <span
                        key={i}
                        className={`word${foundWords.includes(mot) ? " filled" : ""}`}
                        data-word={mot}
                        onMouseEnter={(e) => e.currentTarget.textContent = trad}
                        onMouseLeave={(e) => e.currentTarget.textContent = mot}
                      >
                        {mot}
                      </span>
                    );
                  })}
                </div>

              </div>
            </div>

            <EndGameBtn
              onClick={() => {
                if (getTimerTimeRef.current) sendTime(getTimerTimeRef.current());
                checkErrors();
                setTimerIsFinished(true);
              }}
            />
          </>
        )}
      </div>

      {/* <AnimatedBg /> */}
      <GameMenu onSendLeaveGame={handleLeaveGame} />
      <Popup time={timeElapsed} timerIsFinished={timerIsFinished} errorCount={errorCount} />
      <LeaveGamePopup popupOpen={leaveGamePopup} onSendLeaveGame={handleLeaveGame} />
    </div>
  );
}